import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signInWithRedirect,
  signOut,
} from "@aws-amplify/auth";

import { CognitoUser, CognitoUserPool } from "amazon-cognito-identity-js";

let refreshTimer = null;
let refreshPromise = null;
let visibilityListener = null; // 🔑 NEW
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

export class AuthService {
  static async signInWithMSID(providerName = "USE-MSID") {
    try {
      await signOut({ global: false });
      signInWithRedirect({ provider: { custom: providerName } });
    } catch (error) {
      console.error("Error during Azure-AD sign-in:", error);
    }
  }

  static async getCurrentUser() {
    try {
      return await getCurrentUser();
    } catch (error) {
      if (error !== "The user is not authenticated") {
        console.error("Error fetching current user:", error);
      }
      return null;
    }
  }

  static async getUserAttributes() {
    try {
      return await fetchUserAttributes();
    } catch (error) {
      console.error("Error fetching user attributes:", error);
      return null;
    }
  }

  static hasRefreshToken() {
    try {
      const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
      const userKey = localStorage.getItem(
        `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`,
      );
      if (!userKey) return false;

      const refreshToken = localStorage.getItem(
        `CognitoIdentityServiceProvider.${clientId}.${userKey}.refreshToken`,
      );
      return !!refreshToken;
    } catch {
      return false;
    }
  }

  static getTokenExpiryFromStorage() {
    try {
      const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
      const userKey = localStorage.getItem(
        `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`,
      );
      if (!userKey) return null;

      const accessToken = localStorage.getItem(
        `CognitoIdentityServiceProvider.${clientId}.${userKey}.accessToken`,
      );
      if (!accessToken) return null;

      const payload = JSON.parse(atob(accessToken.split(".")[1]));
      return payload.exp * 1000;
    } catch {
      return null;
    }
  }

  static shouldRefreshToken() {
    if (!this.hasRefreshToken()) return false;

    const expiryTime = this.getTokenExpiryFromStorage();
    if (!expiryTime) return false;

    const timeLeft = expiryTime - Date.now();
    return timeLeft < REFRESH_BUFFER_MS && timeLeft > 0;
  }

  static isTokenExpired() {
    const expiryTime = this.getTokenExpiryFromStorage();
    if (!expiryTime) return true;
    return Date.now() >= expiryTime;
  }

  static async refreshToken() {
    if (refreshPromise) {
      console.log("⏳ Refresh already in progress, waiting...");
      return refreshPromise;
    }

    refreshPromise = this._doRefresh();

    try {
      return await refreshPromise;
    } finally {
      refreshPromise = null;
    }
  }

  static async _doRefresh() {
    console.log("🔄 Starting token refresh...");

    if (!this.hasRefreshToken()) {
      console.warn("⚠️ No refresh token available");
      return null;
    }

    const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
    const userPoolId = process.env.REACT_APP_USER_POOL_ID;
    const userKey = localStorage.getItem(
      `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`,
    );

    if (!userKey) throw new Error("No user key found");

    const refreshTokenString = localStorage.getItem(
      `CognitoIdentityServiceProvider.${clientId}.${userKey}.refreshToken`,
    );

    if (!refreshTokenString) throw new Error("No refresh token found");

    const userPool = new CognitoUserPool({
      UserPoolId: userPoolId,
      ClientId: clientId,
    });

    const cognitoUser = new CognitoUser({
      Username: userKey,
      Pool: userPool,
    });

    return new Promise((resolve, reject) => {
      cognitoUser.refreshSession(
        { getToken: () => refreshTokenString },
        (err, session) => {
          if (err) {
            console.error("❌ Refresh failed:", err);
            reject(err);
            return;
          }

          const newIdToken = session.getIdToken().getJwtToken();
          const newAccessToken = session.getAccessToken().getJwtToken();

          localStorage.setItem(
            `CognitoIdentityServiceProvider.${clientId}.${userKey}.idToken`,
            newIdToken,
          );
          localStorage.setItem(
            `CognitoIdentityServiceProvider.${clientId}.${userKey}.accessToken`,
            newAccessToken,
          );

          console.log("✅ Token refreshed successfully");
          this.scheduleNextRefresh();
          resolve(newIdToken);
        },
      );
    });
  }

  static async getIdToken() {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (error) {
      return null;
    }
  }

  static scheduleNextRefresh() {
    this.stopAutoRefresh();

    if (!this.hasRefreshToken()) {
      console.log("⏭️ Skipping auto-refresh (no refresh token)");
      return;
    }

    const expiryTime = this.getTokenExpiryFromStorage();
    if (!expiryTime) {
      console.warn("⚠️ Cannot read token expiry");
      return;
    }

    const now = Date.now();
    const timeUntilRefresh = expiryTime - now - REFRESH_BUFFER_MS;

    if (timeUntilRefresh <= 0) {
      console.log("⚠️ Token expiring soon, refreshing now...");
      this.refreshToken().catch(console.error);
      return;
    }

    const refreshAt = new Date(now + timeUntilRefresh);
    console.log(
      `⏰ Next refresh at ${refreshAt.toLocaleTimeString()} (in ${Math.round(timeUntilRefresh / 60000)} min)`,
    );

    refreshTimer = setTimeout(async () => {
      try {
        console.log("⏰ Auto-refresh triggered");
        await this.refreshToken();
      } catch (error) {
        console.error("Auto-refresh failed:", error);
        refreshTimer = setTimeout(() => this.scheduleNextRefresh(), 60000);
      }
    }, timeUntilRefresh);
  }

  // 🔑 NEW: Setup visibility listener
  static setupVisibilityListener() {
    if (visibilityListener) return;

    visibilityListener = async () => {
      if (document.visibilityState === "visible") {
        console.log("👁️ Tab became active - checking token...");

        // Token expired while away
        if (this.isTokenExpired()) {
          console.log("⚠️ Token expired while away, refreshing...");
          try {
            await this.refreshToken();
          } catch (error) {
            console.error("Refresh on visibility failed:", error);
            await signOut();
            window.location.replace("/login");
          }
          return;
        }

        // Token expiring soon
        if (this.shouldRefreshToken()) {
          console.log("⚠️ Token expiring soon, refreshing...");
          try {
            await this.refreshToken();
          } catch (error) {
            console.error("Refresh on visibility failed:", error);
          }
          return;
        }

        // Reschedule timer (may have drifted)
        this.scheduleNextRefresh();
      }
    };

    document.addEventListener("visibilitychange", visibilityListener);
    console.log("👁️ Visibility listener registered");
  }

  static startAutoRefresh() {
    console.log("✅ Starting auto-refresh");
    this.setupVisibilityListener(); // 🔑 NEW
    this.scheduleNextRefresh();
  }

  static stopAutoRefresh() {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }

    if (visibilityListener) {
      document.removeEventListener("visibilitychange", visibilityListener);
      visibilityListener = null;
    }
  }
}
